import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { resetPasswordPost } from '@uturn/api';
import { getApiErrorMessage } from '@uturn/portal/utils';
import {
	Button,
	cn,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	sonner,
} from '@uturn/ui-kit';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	PASSWORD_MIN_LENGTH,
	PASSWORD_SPECIAL_CHARACTERS,
	type PasswordResetFormValues,
	passwordResetSchema,
} from '../schema';

export function PasswordResetForm() {
	const { t } = useTranslation();
	const { token = '' } = useSearch({ from: '/_auth/reset-password' });

	const form = useForm<PasswordResetFormValues>({
		resolver: zodResolver(passwordResetSchema),
		mode: 'onTouched',
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
	});

	const { mutate, isPending, isSuccess } = useMutation({
		mutationFn: resetPasswordPost,
		onError: (error) => {
			sonner.error(
				getApiErrorMessage(error) ??
					t('general.errors.generic_title', 'Uh oh! Something went wrong.'),
			);
		},
	});

	const onSubmit: SubmitHandler<PasswordResetFormValues> = ({
		password,
		confirmPassword,
	}) => {
		mutate({
			newPassword: password,
			newPassword1: confirmPassword,
			token,
		});
	};

	return (
		<div className={cn('grid gap-6')}>
			{isSuccess ? (
				<p className="text-sm">{t('pages.auth.password_reset.success')}</p>
			) : (
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="grid gap-2">
							<div className="grid gap-1">
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="sr-only">
												{t('general.password', 'Password')}
											</FormLabel>
											<FormControl>
												<Input
													type="password"
													autoComplete="new-password"
													autoCorrect="off"
													minLength={PASSWORD_MIN_LENGTH}
													required={true}
													disabled={isPending}
													placeholder={t(
														'general.password',
														'Password',
													).toString()}
													{...field}
												/>
											</FormControl>
											<FormMessage />
											<FormDescription className="text-xs">
												{t('password_requirements', {
													ns: 'formValidation',
													minLength: PASSWORD_MIN_LENGTH,
													specialCharacters: PASSWORD_SPECIAL_CHARACTERS,
												})}
											</FormDescription>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="confirmPassword"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="sr-only">
												{t(
													'pages.auth.password_reset.form.fields.confirm_password',
													'Confirm password',
												)}
											</FormLabel>
											<FormControl>
												<Input
													type="password"
													autoComplete="new-password"
													autoCorrect="off"
													minLength={PASSWORD_MIN_LENGTH}
													required={true}
													disabled={isPending}
													placeholder={t(
														'pages.auth.password_reset.form.fields.confirm_password',
														'Confirm password',
													).toString()}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								type="submit"
								disabled={isPending || !form.formState.isValid}
							>
								{isPending && (
									<Icon spin className="mr-3" icon={faSpinnerThird} />
								)}
								{t('pages.auth.password_reset.form.submit', 'Reset password')}
							</Button>
						</div>
					</form>
				</Form>
			)}
		</div>
	);
}
